// QuickBooks client wrappers — thin per-domain module over the Firebase
// callables (calendarClient.js shape). Unlike calendarClient, the Firebase
// error CODE is preserved on the rethrown error: the Financials UI branches
// on it (functions/failed-precondition → not-connected or re-lock via
// details.code, functions/permission-denied → no access, etc.).
import { callable } from "../firebase.js";

const call = (name, options) => async (payload = {}) => {
  try {
    const fn = callable(name, options);
    const result = await fn(payload);
    return result?.data;
  } catch (e) {
    const err = new Error(e?.message || "QuickBooks request failed");
    err.code = e?.code || null; // e.g. "functions/failed-precondition"
    err.details = e?.details || null; // e.g. { code: "FIN_LOCK_REQUIRED" }
    throw err;
  }
};

// Step-up auth (FinGate + finSession use these).
export const finStatus = call("finStatus");
export const finUnlock = call("finUnlock");
export const finSetupPassphrase = call("finSetupPassphrase");
export const finLock = call("finLock");

// QuickBooks connection lifecycle (Settings view).
export const qboStartConnect = call("qboStartConnect");
export const qboExchangeCode = call("qboExchangeCode");
export const qboStatus = call("qboStatus");
export const qboDisconnect = call("qboDisconnect");

// Bank statement uploads (Financials v2 slice 1; Settings → Bank data).
// Stores a QFX/CSV export and compares it with QuickBooks (or returns needsAccount); 130 s outlasts the function's 120 s, the SDK default is 70 s.
export const bankUploadStatement = call("bankUploadStatement", { timeout: 130_000 });
// Maps a bank account or card (kind + last4) to a QuickBooks account.
export const bankSetAccount = call("bankSetAccount");

// Read proxy — every action carries the unlock token.
export const callQbo = (action, payload = {}, unlockToken) =>
  call("qboProxy")({ action, unlockToken, ...payload });

/** True when the error means "unlock expired / missing — re-show the gate". */
export const isLockRequired = (err) => err?.details?.code === "FIN_LOCK_REQUIRED";
/** True when the error means "passphrase not created yet". */
export const isSetupRequired = (err) => err?.details?.code === "FIN_SETUP_REQUIRED";
